import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    ActivationWizardWrapper,
    HeaderActivationWizard,
    LoaderWrapper,
    StepsContainer,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardStyled";
import { Accordion } from "app/components/Accordion/Accordion";
import { AccordionHead } from "app/components/Accordion/AccordionHead";
import { ShareBar } from "@similarweb/ui-components";
import { IconButton } from "@similarweb/ui-components";
import { StepsCompletion } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/StepsCompletion";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { EnumWizardItems, getWithInitActivationWizardItems, setActivationWizardItems } from "./ActivationWizardApi";
import { TitleWidget } from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/DigitalSuiteTrialHomePageStyled";
import { PlainTooltip } from "@similarweb/ui-components";
import type {
    IStepState,
    IStepMetadata,
    ICompletionStep,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import {
    EStepState,
    WizardRole,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { ProgressBarContainer, ProgressBarLabel, ShareBarWrapper } from "app/components/Accordion/AccordionHeadStyled";

interface IActivationWizard {
    role: WizardRole;
    steps: Array<React.FC<IStepMetadata>>;
    stepsState: IStepState[];
    onStepSkip: (activeStep: number) => void;
    onStepComplete: (index: number, guildTrackingName: string) => void;
    resetSkippedToIncomplete: () => void;
    onClose: () => void;
    countStepsByState: (StepState: EStepState) => number;
    getCompletionStep: (allStepsSkipped: boolean) => ICompletionStep;
    trackWithGoal: (guid: string, action: string, replacements?: Record<string, string>) => void;
}

export const ActivationWizard: React.FC<IActivationWizard> = ({
    role,
    steps,
    stepsState,
    onStepSkip,
    onStepComplete,
    resetSkippedToIncomplete,
    onClose,
    countStepsByState,
    getCompletionStep,
    trackWithGoal,
}) => {
    const i18n = i18nFilter();
    const stepOpeningDuration = 500;
    const [showStepsCompletion, setShowStepsCompletion] = useState<boolean>(false);
    const [activeStep, setActiveStep] = useState<number | null>(null);
    const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(true);
    const [isStepOpening, setIsStepOpening] = useState<boolean>(false);
    const allStepsSkipped = useMemo<boolean>(() => {
        return countStepsByState(EStepState.SKIP) === steps.length;
    }, [stepsState, steps]);

    const allStepsCompleted = useMemo<boolean>(() => {
        return countStepsByState(EStepState.COMPLETED) === steps.length;
    }, [stepsState, steps]);

    useEffect(() => {
        setIsAccordionOpen(getWithInitActivationWizardItems(EnumWizardItems.IsAccordionOpen));
    }, []);

    useEffect(() => {
        if (stepsState && Object.keys(stepsState).length) {
            setActiveStep(findNextIncompleteStep());
            setShowStepsCompletion(!countStepsByState(EStepState.INCOMPLETE));
            setIsStepOpening(true);
            setTimeout(() => {
                setIsStepOpening(false);
                setIsAccordionOpen(getWithInitActivationWizardItems(EnumWizardItems.IsAccordionOpen));
            }, stepOpeningDuration);
        }
    }, [stepsState]);

    const findNextIncompleteStep = (): number => {
        const firstIncomplete = stepsState?.find((x) => x.state === EStepState.INCOMPLETE);
        const nextIncomplete = stepsState?.find((x) => {
            const isIncomplete = x.state === EStepState.INCOMPLETE;
            const isAfterActiveStep = activeStep === null || x.index > activeStep;
            return isIncomplete && isAfterActiveStep;
        });

        return nextIncomplete?.index ?? firstIncomplete?.index ?? 0;
    };

    const onShowStepClick = (stepName: string, index: number) => {
        trackWithGoal("growth.homepage.wizard.button.showStep", "click", {
            step: `${role}/${index + 1}/${stepName}`,
        });
        setActiveStep(index);
        setIsStepOpening(true);
        setTimeout(() => setIsStepOpening(false), stepOpeningDuration);
    };

    const onClickAccordion = () => {
        setActivationWizardItems(EnumWizardItems.IsAccordionOpen, !isAccordionOpen);
        setIsAccordionOpen((prev) => !prev);
        trackWithGoal("growth.homepage.wizard.button.accordionState", "click", {
            accordionState: !isAccordionOpen ? "accordion open" : "accordion close",
        });
    };

    const revealCompletedSteps = (trackEvent = true) => {
        if (!allStepsCompleted) {
            resetSkippedToIncomplete();
        }

        setActiveStep(null);
        setShowStepsCompletion(false);
        trackEvent && trackWithGoal("growth.homepage.wizard.completion.button.showSteps", "click");
    };

    const getCompletionButtonText = () =>
        allStepsSkipped
            ? i18n("growth.homepage.activationWizard.retakeSteps")
            : i18n("growth.homepage.activationWizard.stepsCompletion.buttons.show");

    const getStepsStatusText = () => {
        if (allStepsSkipped) {
            return i18n("growth.homepage.activationWizard.stepsStatusText.AllStepsSkipped");
        }

        if (allStepsCompleted) {
            return i18n("growth.homepage.activationWizard.stepsStatusText.AllStepsCompleted");
        }

        return i18n("growth.homepage.activationWizard.stepsStatusText", {
            stepsCompletion: countStepsByState(EStepState.COMPLETED),
            stepsSkip: countStepsByState(EStepState.SKIP),
        });
    };

    const onCompletionButtonClick = (callback) => {
        revealCompletedSteps();

        if (!allStepsSkipped) {
            onClose();
            callback?.();
        }
    };

    const renderStepsContent = () =>
        steps.map((StepWrapper, index) => (
            <StepWrapper
                key={index}
                role={role}
                index={index}
                isOpen={activeStep === index || (activeStep === null && index === 0)}
                isOpening={activeStep === index ? isStepOpening : null}
                onStepSkip={() => onStepSkip(index)}
                onStepComplete={(guid) => onStepComplete(index, guid)}
                stepState={stepsState.find((stp) => stp.index === index)?.state || EStepState.INCOMPLETE}
                onClickHoverButton={() => onShowStepClick(StepWrapper.name, index)}
                trackWithGoal={trackWithGoal}
            />
        ));

    const CloseActivationWizard = () => {
        trackWithGoal("growth.homepage.wizard.iconButton.close", "click");
        onClose();
        setTimeout(() => revealCompletedSteps(false), stepOpeningDuration);
    };

    // loading to homepage
    return !stepsState ? (
        <LoaderWrapper>
            <PixelPlaceholderLoader
                width={244}
                height={17}
            />
            <PixelPlaceholderLoader
                width={"100%"}
                height={296}
            />
        </LoaderWrapper>
    ) : (
        <ActivationWizardWrapper data-automation="platform-homepage-activation-wizard">
            <HeaderActivationWizard>
                <TitleWidget data-automation={"activation-wizard-title"}>
                    {i18n("growth.homepageTrial.activationWizard.title")}
                </TitleWidget>
                <PlainTooltip
                    tooltipContent={i18n("growth.homepageTrial.activationWizard.closeIcon")}
                    placement={"top"}
                >
                    <div
                        data-automation={"activation-wizard-close"}
                        style={{ width: "fit-content" }}
                    >
                        <IconButton
                            iconName="clear"
                            onClick={CloseActivationWizard}
                            type="flat"
                            iconSize={"xs"}
                        ></IconButton>
                    </div>
                </PlainTooltip>
            </HeaderActivationWizard>
            <Accordion
                accordionHeadComponent={
                    <AccordionHead
                        isAccordionOpen={isAccordionOpen}
                        title={
                            role === WizardRole.Seo
                                ? i18n("growth.homepage.activationWizard.accordion.title.seo")
                                : i18n("growth.homepage.activationWizard.accordion.title")
                        }
                        headerContent={
                            <ProgressBarContainer>
                                <ProgressBarLabel>
                                    {`${countStepsByState(EStepState.COMPLETED)}/${steps.length}`}
                                </ProgressBarLabel>
                                <ShareBarWrapper>
                                    <ShareBar
                                        value={countStepsByState(EStepState.COMPLETED) / steps.length}
                                        hideChangeValue
                                        hideValue
                                    />
                                </ShareBarWrapper>
                            </ProgressBarContainer>
                        }
                    />
                }
                isOpen={isAccordionOpen}
                onClick={() => {
                    onClickAccordion();
                }}
            >
                {!showStepsCompletion ? (
                    <StepsContainer>{renderStepsContent()}</StepsContainer>
                ) : (
                    <StepsCompletion
                        buttonText={getCompletionButtonText()}
                        statusText={getStepsStatusText()}
                        allStepsSkipped={allStepsSkipped}
                        onShowButtonClick={revealCompletedSteps}
                        onCtaButtonClick={onCompletionButtonClick}
                        completionStepData={getCompletionStep(allStepsSkipped)}
                    />
                )}
            </Accordion>
        </ActivationWizardWrapper>
    );
};
